import { useEffect, useState } from "react";
import axios from "axios";
import { Heart } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { API } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";

export const DonorWall = () => {
  const { t } = useLang();
  const [donors, setDonors] = useState([]);

  useEffect(() => {
    const fetchDonors = () =>
      axios.get(`${API}/donors/recent`).then((r) => setDonors(r.data)).catch(() => {});
    fetchDonors();
    const id = setInterval(fetchDonors, 30000);
    const onVisible = () => document.visibilityState === "visible" && fetchDonors();
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  return (
    <section data-testid="donor-wall" className="relative bg-[#F8F9FA] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <Overline>{t("donorwall.overline")}</Overline>
            <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight text-[#0A1128]">{t("donorwall.title")}</h2>
            <p className="mt-4 text-[#4B5563] md:text-lg">{t("donorwall.sub")}</p>
          </div>
        </Reveal>

        {donors.length === 0 ? (
          <Reveal delay={0.1}>
            <div data-testid="donor-wall-empty" className="mt-12 max-w-md mx-auto text-center border border-dashed border-[#D4AF37]/50 bg-white p-10">
              <Heart size={26} className="mx-auto text-[#D4AF37]" fill="#D4AF37" />
              <p className="mt-4 font-display text-lg text-[#0A1128]">{t("donorwall.empty")}</p>
            </div>
          </Reveal>
        ) : (
          <div className="mt-14 flex flex-wrap justify-center gap-4" data-testid="donor-wall-list">
            {donors.map((d, i) => (
              <Reveal key={i} delay={Math.min(i * 0.04, 0.4)}>
                <div className="flex items-center gap-3 bg-white border border-[#0A1128]/10 pl-4 pr-5 py-3 hover:border-[#D4AF37]/60 transition-colors">
                  <span className="flex items-center justify-center w-8 h-8 bg-[#0A1128] text-[#D4AF37] font-display text-sm">
                    {(d.name || "A").charAt(0)}
                  </span>
                  <div className="leading-tight">
                    <p className="font-medium text-[#0A1128] text-sm">{d.name}</p>
                    {d.amount ? (
                      <p className="text-xs text-[#B8901F]">
                        ${Number(d.amount).toLocaleString()}
                        {d.frequency === "monthly" ? ` / ${t("donorwall.monthly")}` : ""}
                      </p>
                    ) : null}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
